import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import { Button, Card, CardBody, Heading, Text } from '@apollo/orbit';
import React from 'react';
import { useHistory } from 'react-router-dom';

import IconOrb1 from 'src/assets/icon-orb-1.svg';
import IconOrb2 from 'src/assets/icon-orb-2.svg';

import { DeploymentProvider } from '../config/platforms';

export interface DeploymentOptionCardProps {
  provider: DeploymentProvider;
}

/**
 * Card component for displaying deployment provider options.
 * Handles link-based, modal-based, and guided deployment flows.
 */
export const DeploymentOptionCard = ({
  provider,
}: DeploymentOptionCardProps) => {
  const history = useHistory();

  const handleClick = () => {
    if (provider.type === 'link' && provider.url) {
      // For external links, open in new tab
      window.open(provider.url, '_blank', 'noopener,noreferrer');
    } else if (provider.type === 'guided') {
      // For guided deployments, use onClick callback if provided, otherwise use URL navigation
      if (provider.onClick) {
        provider.onClick();
      } else if (provider.url) {
        history.push(provider.url);
      }
    }
  };

  // Show outlink icon only for cloud providers (not for quick deployment options)
  const isQuickDeployment = ['railway', 'render'].includes(provider.id);
  const showOutlinkIcon = provider.type === 'link' && !isQuickDeployment;

  // Use primary variant for quick deployment buttons
  const buttonVariant = isQuickDeployment ? 'primary' : 'secondary';

  return (
    <Card className="h-full overflow-hidden bg-secondary transition-shadow hover:shadow-md">
      <CardBody className="relative flex h-full overflow-hidden !pb-7">
        {/* Grid background */}
        <svg
          className="pointer-events-none absolute inset-0 size-full overflow-hidden opacity-20"
          style={{
            transform: 'perspective(400px) rotateX(40deg)',
            transformOrigin: 'center top',
          }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="grid"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
              x="5"
              y="5"
            >
              <path
                d="M 20 0 L 0 0 0 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
                strokeDasharray="1.5"
              />
            </pattern>
          </defs>
          <rect
            width="200%"
            height="200%"
            x="-50%"
            y="-50%"
            fill="url(#grid)"
          />
        </svg>

        {/* 2-column layout */}
        <div className="grid size-full grid-cols-[1fr_auto] gap-10">
          {/* Left column: Content */}
          <div className="flex flex-col justify-between gap-3">
            <div className="mb-4">
              <Heading variant="heading" size="lg" className="mb-2">
                {provider.name}
              </Heading>
              <Text variant="body" className="text-secondary">
                {provider.description}
              </Text>
            </div>

            <div>
              <Button
                variant={buttonVariant}
                onClick={handleClick}
                rightIcon={showOutlinkIcon ? <IconOutlink /> : undefined}
                data-testid={`deployment-option-${provider.id}`}
              >
                {provider.action}
              </Button>
            </div>
          </div>

          {/* Right column: Orb icons */}
          {(provider.id === 'railway' || provider.id === 'render') && (
            <div className="flex items-center justify-center">
              {provider.id === 'railway' && (
                <IconOrb1 className="size-28 opacity-100" />
              )}
              {provider.id === 'render' && (
                <IconOrb2 className="size-28 opacity-100" />
              )}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};
